import { Component, HostListener, inject } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LangCode, LanguageService } from '../../../core/services/language.service';

type Link = { label: string; routerLink?: string; href?: string; target?: string };
type StoreItem = { img: string; alt: string; href?: string; target?: string };
type Group = { title: string; links: Link[]; store?: { title: string; items: StoreItem[] } };

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [NgFor, NgIf, RouterLink],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss'
})
export class FooterComponent {
  year = new Date().getFullYear();
  langSvc = inject(LanguageService);

  /** Diccionario local del footer (todo traducido por idioma) */
  private dict: Record<LangCode, any> = {
    en: {
      footer: {
        col1Title: 'ALPHATEC SCIENTIFIC®',
        col2TitleA: 'ALPHANAPSIS®',
        col2TitleB: 'BOREAS®',
        col2TitleC: 'ANIMAL CARE',
        cats: {
          chemistry:'Chemistry', hematology:'Hematology',
          immunology:'Immunology', urinalysis:'Urinalysis',
          centrifuges:'Centrifuges', microscopes:'Microscopes'
        },
        lisPlus:'Clinical LIS (Plus)',
        bloodBankRed:'Blood Bank (Red)',
        vetLis:'Vet LIS',
        refrigeration:'Refrigeration line',
        monitors:'Vital Signs Monitors',
        infusion:'Infusion Pumps',
        radiology:'Digital Radiology',
        supportTitle:'SUPPORT & SERVICES',
        corporateEmail:'Corporate email',
        distributors:'Distributors',
        claims:'Complaints book',
        social:'Social networks',
        policies:'Values & policies',
        privacy:'Privacy',
        terms:'Terms & Conditions',
        news:'Get news',
        emailPh:'Email address',
        subscribe:'Subscribe',
        certifications:'Certifications',
        availableOn:'Available on',
        bottom:{ about:'About us', contact:'Contact', terms:'Terms', privacy:'Privacy', top:'Back to top' },
        copyright:'All rights reserved',
        vet: { chem:'Chemistry Vet', hem:'Hematology Vet', imm:'Immunology Vet', uri:'Urinalysis Vet' },
        storeAlt: { app:'Download on the App Store', play:'Get it on Google Play' }
      }
    },
    es: {
      footer: {
        col1Title: 'ALPHATEC SCIENTIFIC®',
        col2TitleA: 'ALPHANAPSIS®',
        col2TitleB: 'BOREAS®',
        col2TitleC: 'CUIDADO ANIMAL',
        cats: {
          chemistry:'Química', hematology:'Hematología',
          immunology:'Inmunología', urinalysis:'Uroanálisis',
          centrifuges:'Centrífugas', microscopes:'Microscopios'
        },
        lisPlus:'LIS Clínico (Plus)',
        bloodBankRed:'Banco de Sangre (Red)',
        vetLis:'Vet LIS',
        refrigeration:'Línea de Refrigeración',
        monitors:'Monitores de Signos Vitales',
        infusion:'Bombas de Infusión',
        radiology:'Radiología Digital',
        supportTitle:'SOPORTE Y SERVICIOS',
        corporateEmail:'Correo corporativo',
        distributors:'Distribuidores',
        claims:'Libro de Reclamaciones',
        social:'Redes sociales',
        policies:'Valores y políticas',
        privacy:'Privacidad',
        terms:'Términos y Condiciones',
        news:'Recibe noticias',
        emailPh:'Correo electrónico',
        subscribe:'Suscribirme',
        certifications:'Certificaciones',
        availableOn:'Disponible en',
        bottom:{ about:'Nosotros', contact:'Contacto', terms:'Términos', privacy:'Privacidad', top:'Volver arriba' },
        copyright:'Todos los derechos reservados',
        vet: { chem:'Química Vet', hem:'Hematología Vet', imm:'Inmunología Vet', uri:'Uroanálisis Vet' },
        storeAlt: { app:'Descargar en App Store', play:'Disponible en Google Play' }
      }
    },
    pt: {
      footer: {
        col1Title: 'ALPHATEC SCIENTIFIC®',
        col2TitleA: 'ALPHANAPSIS®',
        col2TitleB: 'BOREAS®',
        col2TitleC: 'CUIDADO ANIMAL',
        cats: {
          chemistry:'Química', hematology:'Hematologia',
          immunology:'Imunologia', urinalysis:'Urinálise',
          centrifuges:'Centrífugas', microscopes:'Microscópios'
        },
        lisPlus:'LIS Clínico (Plus)',
        bloodBankRed:'Banco de Sangue (Red)',
        vetLis:'Vet LIS',
        refrigeration:'Linha de Refrigeração',
        monitors:'Monitores de Sinais Vitais',
        infusion:'Bombas de Infusão',
        radiology:'Radiologia Digital',
        supportTitle:'SUPORTE & SERVIÇOS',
        corporateEmail:'E-mail corporativo',
        distributors:'Distribuidores',
        claims:'Livro de Reclamações',
        social:'Redes sociais',
        policies:'Valores e políticas',
        privacy:'Privacidade',
        terms:'Termos e Condições',
        news:'Receba novidades',
        emailPh:'E-mail',
        subscribe:'Assinar',
        certifications:'Certificações',
        availableOn:'Disponível em',
        bottom:{ about:'Sobre nós', contact:'Contato', terms:'Termos', privacy:'Privacidade', top:'Topo' },
        copyright:'Todos os direitos reservados',
        vet: { chem:'Química Vet', hem:'Hematologia Vet', imm:'Imunologia Vet', uri:'Urinálise Vet' },
        storeAlt: { app:'Baixar na App Store', play:'Disponível no Google Play' }
      }
    },
    ko: {
      footer: {
        col1Title: 'ALPHATEC SCIENTIFIC®',
        col2TitleA: 'ALPHANAPSIS®',
        col2TitleB: 'BOREAS®',
        col2TitleC: '동물 케어',
        cats: {
          chemistry:'화학', hematology:'혈액학',
          immunology:'면역학', urinalysis:'요검사',
          centrifuges:'원심분리기', microscopes:'현미경'
        },
        lisPlus:'임상 LIS (Plus)',
        bloodBankRed:'혈액은행 (Red)',
        vetLis:'수의학 LIS',
        refrigeration:'냉장 라인',
        monitors:'활력징후 모니터',
        infusion:'주입 펌프',
        radiology:'디지털 영상의학',
        supportTitle:'지원 및 서비스',
        corporateEmail:'회사 이메일',
        distributors:'대리점',
        claims:'고객 불만 접수',
        social:'소셜 네트워크',
        policies:'가치 및 정책',
        privacy:'개인정보',
        terms:'이용약관',
        news:'소식 받기',
        emailPh:'이메일',
        subscribe:'구독',
        certifications:'인증',
        availableOn:'다음에서 사용 가능',
        bottom:{ about:'회사소개', contact:'문의', terms:'약관', privacy:'개인정보', top:'맨 위로' },
        copyright:'판권 소유',
        vet: { chem:'수의 화학', hem:'수의 혈액학', imm:'수의 면역학', uri:'수의 요검사' },
        storeAlt: { app:'App Store에서 다운로드', play:'Google Play에서 받기' }
      }
    }
  };

  col1: Group[] = [];
  col2: Group[] = [];
  soporte!: Group;
  politicas: Link[] = [];

  constructor() {
    this.rebuild();
    this.langSvc.lang$.subscribe(() => this.rebuild());
  }

  t(path: string): string {
    const obj = this.dict[this.langSvc.lang];
    return path.split('.').reduce<any>((acc, k) => acc?.[k], obj) ?? path;
  }

  private rebuild() {
    // 1) Columna 1 (productos + stores debajo)
    this.col1 = [{
      title: this.t('footer.col1Title'),
      links: [
        { label: this.t('footer.cats.chemistry'),    routerLink: '/categoria/chemistry' },
        { label: this.t('footer.cats.hematology'),   routerLink: '/categoria/hematology' },
        { label: `COLOSSO® ${this.t('footer.cats.immunology')}`, routerLink: '/categoria/immunology' },
        { label: `ALPHAQUARTZ® ${this.t('footer.cats.urinalysis')}`, routerLink: '/categoria/urinalysis' },
        { label: `EOLO® ${this.t('footer.cats.centrifuges')}`, routerLink: '/categoria/centrifuges' },
        { label: `ASTREO® ${this.t('footer.cats.microscopes')}`, routerLink: '/categoria/microscopes' },
      ],
      store: {
        title: this.t('footer.availableOn'),
        items: [
          { img: 'assets/store/app_store.png',   alt: this.t('footer.storeAlt.app'), href: 'https://www.apple.com/app-store/' },
          { img: 'assets/store/google_play.png', alt: this.t('footer.storeAlt.play'), href: 'https://play.google.com/store/apps/details?id=io.alphanapsisplus.com' },
        ]
      }
    }];

    // 2) Columna 2 (software, boreas, cuidado animal) – todo traducido
    this.col2 = [
      {
        title: this.t('footer.col2TitleA'),
        links: [
          { label: this.t('footer.lisPlus'), routerLink: '/software/lis' },
          { label: this.t('footer.bloodBankRed'), routerLink: '/software/blood-bank' },
          { label: this.t('footer.vetLis'), routerLink: '/software/vet-lis' },
        ]
      },
      {
        title: this.t('footer.col2TitleB'),
        links: [{ label: this.t('footer.refrigeration'), routerLink: '/categoria/refrigeration' }]
      },
      {
        title: this.t('footer.col2TitleC'),
        links: [
          { label: this.t('footer.vet.chem'), routerLink: '/categoria/chemistry?vet=1' },
          { label: this.t('footer.vet.hem'),  routerLink: '/categoria/hematology?vet=1' },
          { label: this.t('footer.vet.imm'),  routerLink: '/categoria/immunology?vet=1' },
          { label: this.t('footer.vet.uri'),  routerLink: '/categoria/urinalysis?vet=1' },
          { label: this.t('footer.monitors'), routerLink: '/categoria/monitors' },
          { label: this.t('footer.infusion'), routerLink: '/categoria/infusion' },
          { label: this.t('footer.radiology'), routerLink: '/categoria/radiology' },
        ]
      }
    ];

    // 3) Columna 3 (soporte / políticas)
    this.soporte = {
      title: this.t('footer.supportTitle'),
      links: [
        { label: this.t('footer.corporateEmail'), href: 'mailto:info@alphatecscientific.com' },
        { label: this.t('footer.distributors'), routerLink: '/nosotros#distribuidores' },
        { label: this.t('footer.claims'), routerLink: '/reclamaciones' }
      ]
    };

    this.politicas = [
      { label: this.t('footer.privacy'), routerLink: '/privacidad' },
      { label: this.t('footer.terms'),   routerLink: '/terminos' },
    ];
  }

  // Volver arriba
  showTop = false;
  @HostListener('window:scroll') onScroll(){
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    this.showTop = y > 280;
  }
  backToTop(){ window.scrollTo({ top: 0, behavior: 'smooth' }); }

  onSubscribe(e: Event) {
    e.preventDefault();
    const email = (e.target as HTMLFormElement)
      .querySelector<HTMLInputElement>('input[type="email"]')?.value;
    if (email) alert(`✔ ${email}`);
  }
}
